/* global googletag */
import React, { useEffect, useRef } from "react";

const GPT_SRC = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";

function loadGPTScript() {
  if (typeof document === "undefined") return Promise.reject(new Error("no-document"));
  const existing = document.querySelector(`script[src^="${GPT_SRC}"]`);
  if (existing) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = GPT_SRC;
    s.async = true;
    s.crossOrigin = "anonymous";
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

function ensureGPTGlobals() {
  const w = window;
  w.googletag = w.googletag || { cmd: [] };
  w.__gptGuard = w.__gptGuard || {
    servicesEnabled: false,
  };
  return w.__gptGuard;
}

/**
 * Fullscreen Rewarded GPT ad (no DOM div required).
 * It opens when `show` becomes true.
 */
export default function GoogleRewarded({
  adUnitPath,
  show,
  onGranted,   // (e) => {}
  onClosed,    // (e) => {}
}) {
  const slotRef = useRef(null);

  useEffect(() => {
    if (!show) return;

    let cleanup = () => {};

    (async () => {
      try {
        await loadGPTScript();
      } catch {
        return;
      }

      const guard = ensureGPTGlobals();

      window.googletag.cmd.push(() => {
        // Enable services once
        if (!guard.servicesEnabled) {
          googletag.pubads().enableSingleRequest();
          googletag.enableServices();
          guard.servicesEnabled = true;
        }

        // Destroy older slot (safety)
        if (slotRef.current && googletag.destroySlots) {
          try { googletag.destroySlots([slotRef.current]); } catch {}
          slotRef.current = null;
        }

        // ✅ Define as OUT-OF-PAGE with REWARDED format (this makes it fullscreen)
        const slot = googletag.defineOutOfPageSlot(
          adUnitPath,
          googletag.enums.OutOfPageFormat.REWARDED
        );

        if (!slot) {
          // Ad couldn't be defined (e.g., misconfigured unit)
          return;
        }

        slot.addService(googletag.pubads());
        slotRef.current = slot;

        // Events
        const onReady = (e) => {
          if (e.slot === slotRef.current) {
            try {
              // Opens the fullscreen UI
              e.makeRewardedVisible();
            } catch {}
          }
        };

        const onGrant = (e) => {
          if (e.slot === slotRef.current) {
            // Reward was granted by the SDK/creative
            onGranted && onGranted(e);
          }
        };

        const onClose = (e) => {
          if (e.slot === slotRef.current) {
            onClosed && onClosed(e);
          }
        };

        googletag.pubads().addEventListener("rewardedSlotReady", onReady);
        googletag.pubads().addEventListener("rewardedSlotGranted", onGrant);
        googletag.pubads().addEventListener("rewardedSlotClosed", onClose);

        // Request the ad
        googletag.pubads().refresh([slot]);

        cleanup = () => {
          // removeEventListener exists on modern GPT; guard it
          googletag.pubads().removeEventListener?.("rewardedSlotReady", onReady);
          googletag.pubads().removeEventListener?.("rewardedSlotGranted", onGrant);
          googletag.pubads().removeEventListener?.("rewardedSlotClosed", onClose);
          if (slotRef.current && googletag.destroySlots) {
            try { googletag.destroySlots([slotRef.current]); } catch {}
            slotRef.current = null;
          }
        };
      });
    })();

    return () => cleanup();
  }, [show, adUnitPath, onGranted, onClosed]);

  // Rewarded is fullscreen; no container needed
  return null;
}
